import { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowRight, Box } from 'lucide-react';

import iconCad from '../../assets/drawings/icon-cad.webp';
import iconDraw from '../../assets/drawings/icon-draw.webp';
import iconExample from '../../assets/drawings/icon-example.webp';
import step3d from '../../assets/drawings/step-3d.webp';
import stepAreas from '../../assets/drawings/step-areas.webp';
import stepPlan from '../../assets/drawings/step-plan.webp';
import { PLAN_FILE_ACCEPT } from '../new-project-cad/planFiles';

interface DrawingsEmptyStateProps {
  /** A plan file was picked: a new drawing imports it. */
  onImport: (file: File) => void;
  /** A blank canvas to draw on. */
  onDraw: () => void;
  onOpenExample: () => void;
  /** A project straight from a 3D model (the new project dialog). */
  onCreateProject: () => void;
}

const buttonBase =
  'h-10 w-full max-w-[260px] rounded-full text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-lime-400 transition-colors';
const secondaryButton = `${buttonBase} border border-white/80 text-white hover:bg-white/10`;

// Step pictures grow with the window height, so a laptop screen shows the whole page without
// scrolling and a tall screen does not look empty.
const stepImageHeight =
  'h-24 [@media(min-height:900px)]:h-32 [@media(min-height:1000px)]:h-40 [@media(min-height:1200px)]:h-52';

const Option = ({
  icon,
  title,
  text,
  extra,
  action,
}: {
  icon: string;
  title: string;
  text: string;
  extra?: ReactNode;
  action: ReactNode;
}) => (
  <div className="flex flex-col items-center text-center gap-2 rounded-xl border border-greyP4 bg-black/40 px-5 py-5">
    <img src={icon} alt="" className="h-11 w-auto object-contain [@media(min-height:900px)]:h-14" />
    <h3 className="text-white text-base font-medium">{title}</h3>
    <p className="text-sm text-gray-400 leading-snug max-w-[280px]">{text}</p>
    {extra}
    <div className="mt-auto pt-2 w-full flex justify-center">{action}</div>
  </div>
);

/**
 * The Drawings tab before the first drawing: what a drawing is for (a site plan that becomes a 3D
 * model and then a lighting project) and the three ways to start, plus the shortcut for users who
 * already have a 3D model. Laid out to fit a laptop screen without scrolling.
 */
export function DrawingsEmptyState({ onImport, onDraw, onOpenExample, onCreateProject }: DrawingsEmptyStateProps) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);

  const steps = [
    { image: stepPlan, label: t('drawings.empty.step1') },
    { image: stepAreas, label: t('drawings.empty.step2') },
    { image: step3d, label: t('drawings.empty.step3') },
  ];

  return (
    <div className="max-w-[1280px] pb-6">
      <div className="grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center">
        <div>
          <h1 className="text-white text-2xl md:text-[32px] font-semibold tracking-[-0.02em] leading-tight">
            {t('drawings.empty.title')}
          </h1>
          <p className="mt-2 text-sm md:text-base text-gray-400">{t('drawings.empty.subtitle')}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-400">{t('drawings.empty.haveModel')}</span>
            <button
              onClick={onCreateProject}
              className="inline-flex h-9 items-center gap-2 rounded-full border border-white/80 px-4 text-sm font-medium text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-lime-400 transition-colors"
            >
              <Box className="h-4 w-4" />
              {t('drawings.empty.createProject')}
            </button>
          </div>
        </div>

        <ol className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-2 rounded-xl border border-greyP4 bg-black/40 px-4 py-3">
          {steps.map((step, index) => (
            <li key={step.label} className="contents">
              {index > 0 && <ArrowRight className="w-5 h-5 text-lime-400 justify-self-center" aria-hidden />}
              <div className="flex flex-col items-center gap-1.5 min-w-0">
                <div className={`relative w-full ${stepImageHeight} flex items-center justify-center`}>
                  <img src={step.image} alt="" className="max-h-full max-w-full object-contain" />
                  {index === 1 && (
                    <>
                      <span className="absolute right-0 top-[4%] rounded bg-black/80 border border-white/10 px-1.5 py-0.5 text-[10px] text-white whitespace-nowrap">
                        {t('drawings.empty.sidewalkLabel')}
                      </span>
                      <span className="absolute left-0 bottom-[8%] rounded bg-black/80 border border-white/10 px-1.5 py-0.5 text-[10px] text-white whitespace-nowrap">
                        {t('drawings.empty.buildingLabel')}
                      </span>
                    </>
                  )}
                </div>
                <span className="text-white text-xs md:text-sm text-center">
                  {index + 1}. {step.label}
                </span>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-3">
        <Option
          icon={iconCad}
          title={t('drawings.empty.importTitle')}
          text={t('drawings.empty.importText')}
          extra={
            <div className="flex flex-wrap justify-center gap-1.5">
              {['DWG', 'DXF', 'SVG', t('drawings.empty.image')].map((format) => (
                <span key={format} className="rounded-full bg-greyP5 px-2.5 py-0.5 text-xs text-gray-300">
                  {format}
                </span>
              ))}
            </div>
          }
          action={
            <>
              <input
                ref={fileInput}
                type="file"
                accept={PLAN_FILE_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) onImport(file);
                }}
              />
              <button
                onClick={() => fileInput.current?.click()}
                className={`${buttonBase} bg-white text-black hover:bg-gray-200`}
              >
                {t('drawings.empty.importButton')}
              </button>
            </>
          }
        />
        <Option
          icon={iconDraw}
          title={t('drawings.empty.drawTitle')}
          text={t('drawings.empty.drawText')}
          action={
            <button onClick={onDraw} className={secondaryButton}>
              {t('drawings.empty.drawButton')}
            </button>
          }
        />
        <Option
          icon={iconExample}
          title={t('drawings.empty.exampleTitle')}
          text={t('drawings.empty.exampleText')}
          action={
            <button onClick={onOpenExample} className={secondaryButton}>
              {t('drawings.empty.exampleButton')}
            </button>
          }
        />
      </div>
    </div>
  );
}
